import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canList } from "../common/permissionUtils";

import Loader from "../common/Loader.jsx";
import filterCalender from "../../assets/svg/filterCalender.svg";
import downloadReport from "../../assets/svg/downloadReport.svg";
import greenArrow from "../../assets/svg/greenArrowUp.svg";
import redArrow from "../../assets/png/redArrowDown.png";
import {
  getLeadCountApi,
  getLineChartDataApi,
  getLeadSourceDataApi,
  getLeadOwnerDataApi,
  getLeadStageDataApi,
  getCountryBasedDataApi,
  getCityBasedDataApi,
  convertStatusCount,
  getLeadConvertionRate,
  getConvertedAndLostLeads,
} from "./dashboard-api.js";
import { getTodayCallsApi, getWeekCallsApi } from "../calls/calls-api.js";
import { useNavigate } from "react-router-dom";
import "../common/Util.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  ReferenceLine,
  Tooltip,
} from "recharts";

import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

ChartJS.register(ArcElement, ChartTooltip, Legend);

// VAPT: Constants extracted for reusability
const CHART_COLORS = {
  funnel: ["#6929E2", "#8B53F5", "#A373FF", "#BF9DFF", "#DECDFF", "#EDE4FF"],
  stackedBar: [
    "#A77BE0",
    "#878EE9",
    "#2ca70aff",
    "#FF8787",
    "#F8CA49",
    "#d70334ff",
    "#1ff8ffff",
    "#279af8ff",
    "#c2fb84ff",
    "#fac525ff",
    "#ff00eeff",
  ],
};

// Reusable loading spinner component
const LoadingSpinner = ({ height = "300px" }) => <Loader height={height} />;

export const Dashboard = () => {
  const navigate = useNavigate();
  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasLeadsAccess = canList(rolePermissionList, "Leads");
  const hasContactsAccess = canList(rolePermissionList, "Contacts");
  const hasAccountsAccess = canList(rolePermissionList, "Accounts");
  const hasQuotationAccess = canList(rolePermissionList, "Quotation");
  const hasOrderAccess = canList(rolePermissionList, "Order");
  const hasInvoiceAccess = canList(rolePermissionList, "Invoice");
  const hasPaymentAccess = canList(rolePermissionList, "Payment");
  const hasCallsAccess = canList(rolePermissionList, "Calls");

  // Today's Follow-up / Today's Calls
  const [todayCalls, setTodayCalls] = useState([]);
  const [todayCallsLoading, setTodayCallsLoading] = useState(true);

  // This Week's Follow-up / Week Calls
  const [weekCalls, setWeekCalls] = useState([]);
  const [weekCallsLoading, setWeekCallsLoading] = useState(true);

  // cards api
  const [leadData, setLeadData] = useState([]);
  const [leadTime, setLeadTime] = useState([]);
  const [leadSource, setLeadSource] = useState([]);
  const [leadOwner, setLeadOwner] = useState([]);
  const [monthlyStatusData, setMonthlyStatusData] = useState([]);
  const [locationCount, setLocationCount] = useState([]);
  const [convertCount, setConvertCount] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRef = useRef(null);
  const [ownerData, setOwnerData] = useState([]);
  const [funnelStages, setFunnelStages] = useState([]);
  const [winRate, setWinRate] = useState(0);
  const [lostRate, setLostRate] = useState(0);
  const [activeLabel, setActiveLabel] = useState(null);

  //  progressBar
  const [activeTab, setActiveTab] = useState("countries");
  const [countryBasedLead, setCountryBasedLead] = useState([]);
  const [cityBasedLead, setCityBasedLead] = useState([]);

  const [cardLoading, setCardLoading] = useState(true);
  const [lineChartLoading, setLineChartLoading] = useState(true);
  const [sourceChartLoading, setSourceChartLoading] = useState(true);
  const [ownerChartLoading, setOwnerChartLoading] = useState(true);
  const [countryLeadChartLoading, setCountryLeadChartLoading] = useState(true);
  const [funnelChartLoading, setFunnelChartLoading] = useState(true);

  // Today's Follow-up / Today's Calls
  useEffect(() => {
    if (!hasCallsAccess) {
      setTodayCallsLoading(false);
      return;
    }
    const fetchTodayCalls = async () => {
      try {
        const response = await getTodayCallsApi();
        setTodayCalls(response?.data?.calls || []);
      } catch (err) {
        setError("Failed to load today's calls");
      } finally {
        setTodayCallsLoading(false);
      }
    };

    fetchTodayCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCallsAccess]);

  // This Week's Follow-up / Week Calls
  useEffect(() => {
    if (!hasCallsAccess) {
      setWeekCallsLoading(false);
      return;
    }
    const fetchWeekCalls = async () => {
      try {
        const response = await getWeekCallsApi();
        setWeekCalls(response?.data?.calls || []);
      } catch (err) {
        setError("Failed to load this week's calls");
      } finally {
        setWeekCallsLoading(false);
      }
    };

    fetchWeekCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCallsAccess]);

  //Lead count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await getLeadCountApi();
        setLeadData(response?.data || []);
      } catch (err) {
        setError("Failed to load Time data");
      } finally {
        setCardLoading(false);
      }
    };

    fetchCount();
  }, []);
  //Lead overtime
  useEffect(() => {
    const fetchLineChart = async () => {
      try {
        const response = await getLineChartDataApi();
        setLeadTime(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        setError("Failed to load Time data");
        setLeadTime([]);
      } finally {
        setLineChartLoading(false);
      }
    };

    fetchLineChart();
  }, []);

  //Lead overtime
  useEffect(() => {
    const fetchLineSource = async () => {
      try {
        const response = await getLeadSourceDataApi();
        setLeadSource(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        setError("Failed to load Source data");
        setLeadSource([]);
      } finally {
        setSourceChartLoading(false);
      }
    };

    fetchLineSource();
  }, []);

  //Lead owner
  useEffect(() => {
    const fetchLeadOwner = async () => {
      try {
        const response = await getLeadOwnerDataApi();
        setLeadOwner(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        setError("Failed to load Source data");
        setLeadOwner([]);
      } finally {
        setOwnerChartLoading(false);
      }
    };

    fetchLeadOwner();
  }, []);
  // Fetch funnel chart data
  useEffect(() => {
    const fetchFunnelChart = async () => {
      try {
        const response = await getLeadStageDataApi();
        setFunnelStages(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        setError("Failed to load funnel chart data");
        setFunnelStages([]);
      } finally {
        setFunnelChartLoading(false);
      }
    };
    fetchFunnelChart();
  }, []);

  useEffect(() => {
    if (leadOwner && Array.isArray(leadOwner)) {
      setOwnerData(
        leadOwner.map((item) => ({
          ...item,
        })),
      );
    }
  }, [leadOwner]);

  const maxOwnerCount = Math.max(...(leadOwner || []).map((item) => item.count || 0), 0);
  const xAxisMax = Math.ceil((maxOwnerCount + 2) / 5) * 5;
  const xAxisStep = xAxisMax > 10 ? Math.ceil(xAxisMax / 5) : 1;

  useEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext("2d");

    if (chartRef.current.chart) {
      chartRef.current.chart.destroy();
    }

    const chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: (leadOwner || []).map((item) => item.name),
        datasets: [
          {
            data: (leadOwner || []).map((item) => item.count),
            backgroundColor: (leadOwner || []).map(() => "#c4b5fd"),
            hoverBackgroundColor: (leadOwner || []).map(() => "#6b46c1"),
            borderRadius: 6,
            barThickness: 24,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            backgroundColor: "#2d3748",
            titleColor: "#fff",
            bodyColor: "#fff",
            padding: 12,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              title: () => "",
              label: (context) => context.parsed.x.toString(),
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            max: xAxisMax,
            ticks: {
              stepSize: xAxisStep,
              callback: (count) =>
                count >= 1000 ? (count / 1000).toFixed(1) + "K" : count,
              color: "#6b7280",
              font: { size: 12 },
            },
            grid: { display: false, drawBorder: false },
          },
          y: {
            ticks: {
              color: "#6b7280",
              font: { size: 14 },
              padding: 10,
            },
            grid: { display: false, drawBorder: false },
          },
        },
        layout: {
          padding: { right: 80 },
        },
      },
      plugins: [
        {
          id: "customLabels",
          afterDatasetsDraw: (chart) => {
            const ctx = chart.ctx;
            chart.data.datasets.forEach((dataset, i) => {
              const meta = chart.getDatasetMeta(i);
              meta.data.forEach((bar, index) => {
                const data = dataset.data[index];
                if (data > 0) {
                  const x = bar.x + bar.width + 10;
                  const y = bar.y + bar.height / 2;

                  ctx.save();
                  ctx.fillStyle = "#2d3748";
                  ctx.font = "bold 12px Arial";
                  ctx.textAlign = "left";
                  ctx.textBaseline = "middle";

                  const text = data.toString();
                  const textWidth = ctx.measureText(text).width;
                  const padding = 8;
                  const rectX = x;
                  const rectY = y - 12;
                  const rectWidth = textWidth + padding * 2;
                  const rectHeight = 24;
                  const radius = 4;

                  if (ctx.roundRect) {
                    ctx.beginPath();
                    ctx.roundRect(rectX, rectY, rectWidth, rectHeight, radius);
                    ctx.fill();
                  } else {
                    ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
                  }

                  ctx.fillStyle = "#ffffff";
                  ctx.fillText(text, rectX + padding, y);
                  ctx.restore();
                }
              });
            });
          },
        },
      ],
    });

    chartRef.current.chart = chart;

    return () => {
      chart.destroy();
    };
  }, [leadOwner, xAxisMax, xAxisStep]);

  const cards = [
    ...(hasLeadsAccess ? [
      {
        title: "Total Leads",
        value: leadData?.total_leads?.total || 0,
        percent: leadData?.total_leads?.diff || 0,
        isDecreased: leadData?.total_leads?.decreased || false,
      },
      {
        title: "New Leads",
        value: leadData?.new_leads?.total || 0,
        percent: leadData?.new_leads?.diff || 0,
        isDecreased: leadData?.new_leads?.decreased || false,
      },
      {
        title: "Total Qualified Leads",
        value: leadData?.qualified_leads?.total || 0,
        percent: leadData?.qualified_leads?.diff || 0,
        isDecreased: leadData?.qualified_leads?.decreased || false,
      },
      {
        title: "Converted Leads",
        value: leadData?.converted_leads?.total || 0,
        percent: leadData?.converted_leads?.diff || 0,
        isDecreased: leadData?.converted_leads?.decreased || false,
      },
      {
        title: "Conversation Rate (%)",
        value: leadData?.leads_ratio?.total || 0,
        percent: leadData?.leads_ratio?.diff || 0,
        isDecreased: leadData?.leads_ratio?.decreased || false,
      },
      {
        title: "Average Response Time",
        value: leadData?.leads_average?.total || 0,
        percent: leadData?.leads_average?.diff || 0,
        isDecreased: leadData?.leads_average?.decreased || false,
      },
    ] : []),
  ];

  // funnel chart
  const totalLeads = leadSource.reduce((sum, item) => sum + item.count, 0);

  const chartData = {
    labels: (leadSource || []).map((item) => item.name),
    datasets: [
      {
        data: (leadSource || []).map((item) => item.count),
        backgroundColor: (leadSource || []).map(
          (item, index) => CHART_COLORS.funnel[index],
        ),
        borderWidth: 0,
        cutout: "75%",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#000",
        bodyColor: "#666",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.parsed}`;
          },
        },
      },
    },
  };

  // Define colors for funnel stages
  const funnelColors = [
    "#EDE4FF",
    "#DECDFF",
    "#BF9DFF",
    "#DECDFF",
    "#8B53F5",
    "#6929E2",
  ];

  // Use API data for funnel chart
  const stages = (funnelStages || []).map((stage, i) => ({
    label: stage.name || "Unknown",
    value: stage.count,
    color: funnelColors[i % funnelColors.length],
  }));

  // Add chartHeight definition
  const chartHeight = 300;

  const CustomActiveDot = (props) => {
    const { cx, cy, chartHeight } = props;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill="#6929E2"
        stroke="#fff"
        strokeWidth={2}
        style={{ filter: "drop-shadow(0 2px 6px rgba(105,41,226,0.2))" }}
      />
    );
  };

  // Fetch Country Data

  useEffect(() => {
    const fetchCountryBasedLead = async () => {
      try {
        setLoading(true);
        const response = await getCountryBasedDataApi();
        setLocationCount(response?.data || []);
      } catch (err) {
        setError("Failed to load Country data");
      } finally {
        setLoading(false);
      }
    };

    fetchCountryBasedLead();
  }, [activeTab]);

  useEffect(() => {
    const fetchCityBasedLead = async () => {
      if (activeTab === "cities" && cityBasedLead.length === 0) {
        setLoading(true);
        try {
          setLoading(true);
          const response = await getCityBasedDataApi();
          setLocationCount(response?.data || []);
        } catch (err) {
          setError("Failed to load City data");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCityBasedLead();
  }, [activeTab]);

  useEffect(() => {
    const convertedStatusCount = async () => {
      try {
        const response = await convertStatusCount();
        setMonthlyStatusData(response?.data || []);
      } catch (err) {
        setError("Failed to load Source data");
      } finally {
        setOwnerChartLoading(false);
      }
    };

    convertedStatusCount();
  }, []);

  const currentData =
    activeTab === "countries" ? countryBasedLead : cityBasedLead;

  const styles = {
    title: {
      color: "#172B4D",
      marginBottom: "24px",
    },
    tab: {
      padding: "8px 20px",
      border: "1px solid #EBEBEB",
      background: "transparent",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "400",
      color: "#21272C",
      borderRadius: "50px",
      transition: "all 0.2s",
    },
    activeTab: {
      padding: "8px 20px",
      border: "none",
      backgroundColor: "#EFE6FF",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "600",
      color: "#6929E2",
      borderRadius: "50px",
      transition: "all 0.2s",
    },

    itemHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "8px",
    },
    countryName: {
      fontSize: "13px",
      color: "#8790A1",
      fontWeight: "400",
    },
    count: {
      fontSize: "16px",
      color: "#19324C",
      fontWeight: "600",
    },
    progressBarContainer: {
      width: "100%",
      height: "12px",
      backgroundColor: "#f3f4f6",
      borderRadius: "50px",
      overflow: "hidden",
    },
    progressBar: {
      height: "100%",
      backgroundColor: "#7c3aed",
      borderRadius: "50px",
      transition: "width 0.3s ease",
    },
  };

  const [data, setData] = useState([]);
  const [yAxisDomain, setYAxisDomain] = useState([-15, 15]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getConvertedAndLostLeads();
        if (res?.success && Array.isArray(res?.data)) {
          const formatted = res.data.map((item) => ({
            month: item.month,
            converted: item.qualified ?? 0,
            lost: -(item.lost ?? 0),
            qual_diff: item.qual_diff ?? 0,
            lost_diff: item.lost_diff ?? 0,
            qual_dec: item.qual_dec ?? false,
            lost_dec: item.lost_dec ?? false,
          }));

          setData(formatted);

          // find the maximum & minimum values
          const maxQualified = Math.max(...formatted.map((d) => d.converted), 0);
          const maxLost = Math.min(...formatted.map((d) => d.lost), 0); // negative side

          // add 10–20% padding for spacing
          setYAxisDomain([maxLost * 1.2, maxQualified * 1.2]);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setData([]);
      }
    };

    fetchData();
  }, []);
  //  Improved tick generator
  const generateTicks = (min, max, count = 6) => {
    const range = max - min;
    if (range <= 0) return [min, max];
    const step = range / (count - 1);
    return Array.from({ length: count }, (_, i) => Math.round(min + i * step));
  };

  const CustomConvertedTooltip = ({ active, payload, coordinate }) => {
    if (!active || !payload?.length) return null;
    const converted = payload.find((p) => p.dataKey === "converted");
    if (!converted) return null;

    const qualDiff = converted.payload?.qual_diff ?? 0;
    const decreasedConverted = converted.payload?.qual_dec ?? 0;
    return (
      <div
        className="d-flex flex-column justify-content-between"
        style={{
          width: "200px",
          height: "63px",
          position: "absolute",
          left: coordinate.x - 100,
          top: coordinate.y - 35,
          transform: "translate(-50%, -100%)",
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          padding: "6px 10px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          fontSize: "12px",
        }}
      >
        <div className="fs-13 fw-600 d-flex align-items-center justify-content-between">
          Converted:
          <span
            className="fs-16 fw-600"
            style={{ color: "#6929E2", marginLeft: "5px" }}
          >
            {converted.value}
          </span>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <img
            src={decreasedConverted ? redArrow : greenArrow}
            alt={decreasedConverted ? "red arrow" : "green arrow"}
            style={{ width: "10px" }}
          />

          <span className="fs-11 fw-600" style={{ color: "#2C8C53" }}>
            {qualDiff}%
          </span>
          <span className="fs-12 fw-400" style={{ color: "#66737E" }}>
            From previous month
          </span>
        </div>
      </div>
    );
  };

  const CustomLostTooltip = ({ active, payload, coordinate }) => {
    if (!active || !payload?.length) return null;
    const lost = payload.find((p) => p.dataKey === "lost");
    if (!lost) return null;
    const lostDiff = lost.payload?.qual_diff ?? 0;
    const decreasedLost = lost.payload?.lost_dec ?? 0;

    return (
      <div
        className="d-flex flex-column justify-content-between"
        style={{
          width: "200px",
          height: "63px",
          position: "absolute",
          left: coordinate.x - 100,
          top: coordinate.y + 20,
          transform: "translate(-50%, 0)",
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          padding: "6px 10px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          fontSize: "12px",
          color: "#ef4444",
          fontWeight: "600",
          pointerEvents: "none",
        }}
      >
        <div className="fs-13 fw-600 d-flex align-items-center justify-content-between">
          Lost:
          <span
            className="fs-16 fw-600"
            style={{ color: "#FF4444", marginLeft: "5px" }}
          >
            {lost.value}
          </span>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <img
            src={decreasedLost ? greenArrow : redArrow}
            alt={decreasedLost ? "red arrow" : "green arrow"}
            style={{ width: "10px" }}
          />
          <span
            className="fs-11 fw-600"
            style={{ color: decreasedLost ? "#2C8C53" : "#FF4444" }}
          >
            {lostDiff}%
          </span>
          <span className="fs-12 fw-400" style={{ color: "#66737E" }}>
            From previous month
          </span>
        </div>
      </div>
    );
  };

  //  vertical stacked bar chart

  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const colorPalette = [
    "#A77BE0",
    "#878EE9",
    "#2ca70aff",
    "#FF8787",
    "#F8CA49",
    "#d70334ff",
    "#1ff8ffff",
    "#279af8ff",
    "#c2fb84ff",
    "#fac525ff",
    "#ff00eeff",
  ];

  const [stackedBarChartData, setStackedBarChartData] = useState(() => {
    return {
      months: [],
      data: [],
      colors: {
        pending: colorPalette[0],
        attempted_to_contact: colorPalette[1],
        contact_in_future: colorPalette[2],
        contacted: colorPalette[3],
        junk_lead: colorPalette[4],
        lost_lead: colorPalette[5],
        not_contacted: colorPalette[6],
        pre_qualified: colorPalette[7],
        not_qualified: colorPalette[8],
      },
    };
  });

  const stackedBarChartHeight = 200;
  const barWidth = 10;
  const barSpacing = 25;

  const getBarHeight = (value) => {
    const maxVal = stackedBarChartData.maxValue || 1;
    return (value / maxVal) * stackedBarChartHeight;
  };

  const handleMouseEnter = (index, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredBar(index);
    setTooltipPos({ x: rect.left + rect.width, y: rect.top - 10 });
  };

  const handleMouseLeave = () => {
    setHoveredBar(null);
  };

  const renderYAxisLabels = () => {
    const steps = 6;
    const maxVal = stackedBarChartData.maxValue || 1;
    const stepValue = maxVal / steps;

    return Array.from({ length: steps + 1 }, (_, i) => {
      const label = Math.round(i * stepValue);
      return (
        <div
          key={i}
          style={{
            position: "absolute",
            left: "0",
            top: `${
              stackedBarChartHeight - (i * stackedBarChartHeight) / steps + 25
            }px`,
            fontSize: "11px",
            color: "#999",
          }}
        >
          {label}
        </div>
      );
    });
  };

  useEffect(() => {
    if (monthlyStatusData && monthlyStatusData.length > 0) {
      const statusKeys = Object.keys(stackedBarChartData.colors);

      const totals = monthlyStatusData.map((item) =>
        statusKeys.reduce((sum, key) => sum + (item[key] || 0), 0),
      );

      const maxValue = Math.max(...totals, 1);

      setStackedBarChartData((prev) => ({
        ...prev,
        months: monthlyStatusData.map((item) => item.month),
        data: monthlyStatusData,
        maxValue,
      }));
    }
  }, [monthlyStatusData]);

  const tooltipLabels = {
    pending: "Pending",
    attempted_to_contact: "Attempted to Contact",
    contact_in_future: "Contact in Future",
    contacted: "Contacted",
    junk_lead: "Junk Lead",
    lost_lead: "Lost Lead",
    not_contacted: "Not Contacted",
    pre_qualified: "Pre-Qualified",
    not_qualified: "Not Qualified",
  };

  // final progress bar

  useEffect(() => {
    const fetchConversionRate = async () => {
      try {
        const response = await getLeadConvertionRate();

        if (response.success && response.data) {
          setWinRate(response.data.win_percent || 0);
          setLostRate(response.data.lost_percent || 0);
        } else {
          setError("No data found");
        }
      } catch (err) {
        setError("Failed to fetch conversion rate");
      } finally {
        setLoading(false);
      }
    };

    fetchConversionRate();
  }, []);

  // Calculate total
  const total = winRate + lostRate;
  const winPercent = total ? (winRate / total) * 100 : 0;
  const lostPercent = total ? (lostRate / total) * 100 : 0;

  return (
    <div className="w-100 p-3 h-100" style={{ backgroundColor: "#F7F8FC" }}>
      {/* Header */}
      <div className="d-flex justify-content-between w-100">
       {/*  <p className="fs-20 fw-700 textDarkBlue">Dashboard</p> */}
       <div className="header-tabs">
          <button  onClick={() => navigate("/dashboard")} className="dashboard-tab-btn  active">Leads Hub</button>
          <button  onClick={() => navigate("/revenue")} className="dashboard-tab-btn ">Revenue Overview</button>
          <button onClick={() => navigate("/pipeline")} className="dashboard-tab-btn">Pipeline</button>
          <button onClick={() => navigate("/forecast")} className="dashboard-tab-btn">Forecast</button>
          <button onClick={() => navigate("/next-action")} className="dashboard-tab-btn">Next Actions</button>
          {/* <button  onClick={() => navigate("/salesperson")} className="dashboard-tab-btn">Sales Performance</button> */}
        </div>
        <div className="d-flex gap-3">
          <button
            className="bg-transparent rounded-pill d-flex align-items-center gap-2 px-3 fs-14 fw-600 textPurple"
            style={{ border: "1px solid #6929E2" }}
          >
            <img src={filterCalender} alt="calender" />
            Filter by date range
          </button>
          <button className="rounded-pill d-flex align-items-center gap-2 px-4 fs-14 fw-600 text-white backgroundPurple border-0">
            <img src={downloadReport} alt="downloadReport" />
            Download report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {cardLoading ? (
        <div className="row mt-3">
          <div className="col-12">
            <LoadingSpinner />
          </div>
        </div>
      ) : (
        <div className="row mt-3">
          {cards.map((card, i) => (
            <div key={i} className="col-xl-2">
              <div
                className="bg-white rounded-4 p-3 d-flex flex-column gap-1"
                style={{ height: "110px", overflowY: "auto" }}
              >
                <p className="textLightGrey fs-13 fw-400 m-0">{card.title}</p>
                <p className="fs-20 fw-600 textDarkBlue1 m-0">{card.value}</p>
                <p className="textDarkBlue fs-13 fw-400 m-0">
                  <span
                    className={`fs-13 fw-600 ${
                      card.isDecreased ? "text-danger" : "textLightGreen"
                    }`}
                  >
                    {card.percent}%
                  </span>
                  &nbsp; from last month
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
<div className="row mt-3">
      {/* Today's Follow-up */}
      {hasCallsAccess && (
        
          <div className="col-xl-6">
            <div className="bg-white rounded-4 p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-semibold m-0">Today's Follow-up</h5>
                <span
                  className="fs-13 fw-600 textPurple"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/calls/list")}
                >
                  View all
                </span>
              </div>
              {todayCallsLoading ? (
                <LoadingSpinner height="120px" />
              ) : todayCalls.length === 0 ? (
                <p className="textLightGrey fs-13 fw-400 m-0 text-center py-3">
                  No calls scheduled for today.
                </p>
              ) : (
                <div className="d-flex flex-column">
                  {todayCalls.map((call, idx) => (
                    <div
                      key={call.id ?? idx}
                      className="d-flex justify-content-between align-items-center py-2"
                      style={{
                        borderBottom:
                          idx !== todayCalls.length - 1 ? "1px solid #F1F2F4" : "none",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate("/calls/view", { state: { id: call.id } })}
                    >
                      <div className="d-flex flex-column">
                        <span className="fs-14 fw-600 textDarkBlue1">
                          {call.title || call.subject || "Untitled call"}
                        </span>
                        <span className="fs-12 fw-400 textLightGrey">
                          {call.call_for}
                          {call.call_type ? ` · ${call.call_type}` : ""}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fs-13 fw-500 textDarkBlue">
                          {call.start_time} - {call.end_time}
                        </span>
                        <span
                          className={`fs-12 fw-600 px-2 py-1 rounded-pill ${
                            call.status === "Completed"
                              ? "textLightGreen"
                              : "textPurple"
                          }`}
                          style={{
                            backgroundColor:
                              call.status === "Completed" ? "#E6FFF1" : "#EFE6FF",
                          }}
                        >
                          {call.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        
      )}

      {/* This Week's Follow-up */}
      {hasCallsAccess && (
        
          <div className="col-xl-6">
            <div className="bg-white rounded-4 p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-semibold m-0">This Week's Follow-up</h5>
                <span
                  className="fs-13 fw-600 textPurple"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/calls/list")}
                >
                  View all
                </span>
              </div>
              {weekCallsLoading ? (
                <LoadingSpinner height="120px" />
              ) : weekCalls.length === 0 ? (
                <p className="textLightGrey fs-13 fw-400 m-0 text-center py-3">
                  No calls scheduled for this week.
                </p>
              ) : (
                <div className="d-flex flex-column">
                  {weekCalls.map((call, idx) => (
                    <div
                      key={call.id ?? idx}
                      className="d-flex justify-content-between align-items-center py-2"
                      style={{
                        borderBottom:
                          idx !== weekCalls.length - 1 ? "1px solid #F1F2F4" : "none",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate("/calls/view", { state: { id: call.id } })}
                    >
                      <div className="d-flex flex-column">
                        <span className="fs-14 fw-600 textDarkBlue1">
                          {call.title || call.subject || "Untitled call"}
                        </span>
                        <span className="fs-12 fw-400 textLightGrey">
                          {call.call_for}
                          {call.call_type ? ` · ${call.call_type}` : ""}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fs-13 fw-500 textDarkBlue">
                          {call.start_date ? `${call.start_date}, ` : ""}
                          {call.start_time} - {call.end_time}
                        </span>
                        <span
                          className={`fs-12 fw-600 px-2 py-1 rounded-pill ${
                            call.status === "Completed"
                              ? "textLightGreen"
                              : "textPurple"
                          }`}
                          style={{
                            backgroundColor:
                              call.status === "Completed" ? "#E6FFF1" : "#EFE6FF",
                          }}
                        >
                          {call.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
       
      )}
 </div>
      {/* Charts */}
      <div className="row mt-3">
        {/* Funnel - Show only if user has Leads permission */}
        {hasLeadsAccess && (
        <div className="col-xl-6">
          <div className="bg-white rounded-4 p-3" style={{ height: "387px", overflow: "hidden" }}>
            <h5 className="fw-semibold mb-3">Leads by Stage</h5>
            {funnelChartLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="d-flex align-items-center justify-content-center gap-5">
                <svg width="300" height="300" viewBox="0 0 300 300">
                  {stages.map((stage, i) => {
                    const heights = [60, 60, 65, 65];
                    const topY = heights?.slice(0, i).reduce((a, b) => a + b, 0);
                    const bottomY = topY + heights[i];

                    const widths = [300, 200, 100, 60, 40];
                    const topWidth = widths[i];
                    const bottomWidth = widths[i + 1];

                    const xCenter = 150;

                    const points = [
                      [xCenter - topWidth / 2, topY],
                      [xCenter + topWidth / 2, topY],
                      [xCenter + bottomWidth / 2, bottomY],
                      [xCenter - bottomWidth / 2, bottomY],
                    ]
                      .map((p) => p.join(","))
                      .join(" ");

                    return (
                      <polygon
                        key={i}
                        points={points}
                        fill={stage.color}
                        stroke="white"
                        strokeWidth="2"
                      />
                    );
                  })}
                </svg>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {stages.map((stage, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minWidth: "150px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            flexShrink: 0,
                            backgroundColor: stage.color,
                          }}
                        />
                        <span style={{ color: "#6c757d", fontSize: "16px" }}>
                          {stage.label}
                        </span>
                      </div>
                      <span
                        style={{
                          color: "#212529",
                          fontWeight: "600",
                          fontSize: "18px",
                        }}
                      >
                        {stage.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Line Chart */}
        <div className="col-xl-6">
          <div className="bg-white p-3 rounded-4" style={{ height: "387px", overflow: "hidden" }}>
            <h5 className="fw-semibold mb-3">Leads over time</h5>
            {lineChartLoading ? (
              <LoadingSpinner />
            ) : (
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <LineChart
                    data={Array.isArray(leadTime) ? leadTime : []}
                    margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="rgba(236,229,255,1)"
                          stopOpacity={1}
                        />
                        <stop
                          offset="100%"
                          stopColor="rgba(255,255,255,0)"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f0f0f0"
                    />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      domain={[0, "dataMax + 50"]}
                    />

                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="none"
                      fill="url(#colorGradient)"
                      baseLine={0}
                    />

                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#6929E2"
                      strokeWidth={3}
                      dot={false}
                      activeDot={(props) => (
                        <CustomActiveDot {...props} chartHeight={chartHeight} />
                      )}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
        

        {/* doughnut chart - Show only if user has Leads permission */}
        {hasLeadsAccess && (
        <div className="col-xl-6 mt-3">
          <div className="bg-white rounded-4 p-3" style={{ height: "275px", overflow: "hidden" }}>
            <h5 className="mb-4 fw-semibold" style={{ color: "#1a202c" }}>
              Leads by source
            </h5>
            {sourceChartLoading ? (
              <LoadingSpinner height="300px" />
            ) : (
              <div className="row align-items-center">
                <div className="col-md-6 col-12 mb-3 mb-md-0">
                  <div style={{ position: "relative", height: "200px" }}>
                    <Doughnut data={chartData} options={options} />
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2.5rem",
                          fontWeight: "700",
                          color: "#1a202c",
                        }}
                      >
                        {totalLeads}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Total leads
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-12">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      maxHeight: "190px",
                      overflowY: "auto",
                      paddingRight: "10px",
                    }}
                  >
                    {(leadSource || []).map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              flexShrink: 0,
                              backgroundColor:
                                stackedBarChartData.colors[index],
                            }}
                          ></div>
                          <span
                            title={item.name}
                            style={{
                              color: "#6b7280",
                              fontSize: "0.8rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.name}
                          </span>
                        </div>
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#1a202c",
                            fontSize: "0.95rem",
                            flexShrink: 0,
                          }}
                        >
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
        {/* horizontal bar chart  */}
        <div className="col-xl-6 mt-3">
          <div className="bg-white rounded-4 p-3" style={{ height: "275px", overflow: "hidden" }}>
            <div
              style={{
                maxWidth: "800px",
                margin: "0 auto",
                padding: "10px 0",
                fontFamily:
                  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
              }}
            >
              <h5
                style={{
                  color: "#1a1a1a",
                  fontSize: "18px",
                  fontWeight: 600,
                  marginBottom: "20px",
                }}
              >
                Leads by owner
              </h5>
              {ownerChartLoading ? (
                <LoadingSpinner height="180px" />
              ) : (
                <div style={{ height: "180px", overflowY: "auto", overflowX: "hidden" }}>
                  <div
                    style={{
                      position: "relative",
                      height: `${Math.max((leadOwner || []).length * 40, 180)}px`,
                    }}
                  >
                    <canvas ref={chartRef}></canvas>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* horizontal progress bar  */}
        <div className="col-xl-6 mt-3">
          <div className="bg-white rounded-4 p-3 h-100">
            <div className="">
              <p style={styles.title} className="fs-20 fw-600">
                Country based leads
              </p>

              <div className="d-flex gap-3">
                <button
                  style={
                    activeTab === "countries" ? styles.activeTab : styles.tab
                  }
                  onClick={() => setActiveTab("countries")}
                >
                  Countries
                </button>
                <button
                  style={activeTab === "cities" ? styles.activeTab : styles.tab}
                  onClick={() => setActiveTab("cities")}
                >
                  Cities
                </button>
              </div>

              <div className="mt-3 " style={{ maxHeight: "300px" }}>
                {loading ? (
                  <LoadingSpinner height="200px" />
                ) : (
                  <div
                    style={{
                      maxHeight: "250px",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                      paddingRight: "10px",
                    }}
                  >
                    {(locationCount || []).map((item, index) => {
                      const firstCount = locationCount[0]?.count ?? 1;
                      return (
                        <div key={index} className="mb-3">
                          <div style={styles.itemHeader}>
                            <span style={styles.countryName}>
                              {item.name ?? "Unknown"}
                            </span>
                            <span style={styles.count}>{item.count ?? 0}</span>
                          </div>
                          <div style={styles.progressBarContainer}>
                            <div
                              style={{
                                ...styles.progressBar,
                                width: `${
                                  ((item.count ?? 0) / firstCount) * 100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* multi axis line chart  */}
        <div className="col-xl-6 mt-3">
          <div className="bg-white rounded-4 p-3 h-100">
            <div
              style={{
                width: "100%",
                backgroundColor: "#ffffff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <p className="fs-20 fw-600 textDarkBlue">
                  Leads converted and Leaving
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "40%",
                        backgroundColor: "#6929E2",
                      }}
                    ></span>
                    <span className="fs-12 fw-400 textDarkBlue">Converted</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "40%",
                        backgroundColor: "#FF4444",
                      }}
                    ></span>
                    <span className="fs-12 fw-400 textDarkBlue">
                      Lost Leads
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: 300,
                  background: "#fff",
                  overflow: "visible",
                }}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={Array.isArray(data) ? data : []}
                    margin={{ top: 20, right: 30, left: -10, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 0"
                      stroke="#e5e7eb"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#666", fontSize: 12 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => Math.abs(value)} // show positive numbers only
                      tick={{ fill: "#666", fontSize: 12, fontWeight: 500 }}
                      domain={yAxisDomain}
                      ticks={generateTicks(yAxisDomain[0], yAxisDomain[1], 5)}
                      label={{
                        value: "Count",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                          textAnchor: "middle",
                          fill: "#666",
                          fontSize: 12,
                          fontWeight: 600,
                        },
                      }}
                    />

                    <ReferenceLine
                      y={0}
                      stroke="#d1d5db"
                      strokeDasharray="5 5"
                    />
                    <ReferenceLine
                      x="Jun"
                      stroke="#9ca3af"
                      strokeDasharray="5 5"
                    />

                    {/*  Tooltip for Converted */}
                    <Tooltip
                      content={<CustomConvertedTooltip />}
                      cursor={false}
                      wrapperStyle={{ pointerEvents: "none" }}
                      isAnimationActive={false}
                    />

                    {/*  Tooltip for Lost */}
                    <Tooltip
                      content={<CustomLostTooltip />}
                      cursor={false}
                      wrapperStyle={{ pointerEvents: "none" }}
                      isAnimationActive={false}
                    />

                    <Line
                      type="linear"
                      dataKey="converted"
                      stroke="#7C3AED"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{
                        r: 6,
                        stroke: "#7C3AED",
                        strokeWidth: 2,
                        fill: "#fff",
                      }}
                      isAnimationActive={false}
                    />

                    <Line
                      type="linear"
                      dataKey="lost"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{
                        r: 6,
                        stroke: "#ef4444",
                        strokeWidth: 2,
                        fill: "#fff",
                      }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* vertical stacked bar chart  */}

        <div className="col-xl-6 mt-3">
          <div className="bg-white p-3 rounded-4">
            {ownerChartLoading ? (
              <p>Loading...</p>
            ) : error ? (
              <p style={{ color: "red" }}>{error}</p>
            ) : (
              <div
                style={{
                  padding: "30px",
                  backgroundColor: "#fff",
                  position: "relative",
                  overflow: "auto",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Converted Status Count
                </h2>

                <div
                  style={{
                    display: "flex",
                    position: "relative",
                    marginTop: "30px",
                  }}
                >
                  {/* Y-axis */}
                  <div
                    style={{
                      position: "relative",
                      width: "40px",
                      height: `${stackedBarChartHeight}px`,
                      marginRight: "20px",
                    }}
                  >
                    {renderYAxisLabels()}
                  </div>

                  {/* Chart area */}
                  <div
                    style={{
                      position: "relative",
                      flex: 1,
                      marginTop: "30px",
                      marginLeft: "0px",
                    }}
                  >
                    {/* Grid lines */}
                    <div
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: `${stackedBarChartHeight}px`,
                      }}
                    >
                      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                        <div
                          key={i}
                          style={{
                            position: "absolute",
                            width: "100%",
                            height: "1px",
                            backgroundColor: i === 0 ? "#ddd" : "#f5f5f5",
                            top: `${(i * stackedBarChartHeight) / 6}px`,
                          }}
                        />
                      ))}
                    </div>

                    {/* Bars */}
                    <div
                      style={{
                        display: "flex",
                        gap: `${barSpacing}px`,
                        position: "relative",
                        height: `100%`,
                        alignItems: "flex-end",
                      }}
                    >
                      {stackedBarChartData.data.map((data, index) => {
                        const month = data.month;

                        return (
                          <div
                            key={index}
                            style={{
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                            onMouseEnter={(e) => handleMouseEnter(index, e)}
                            onMouseLeave={handleMouseLeave}
                          >
                            {hoveredBar === index && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "-6px",
                                  left: "-3px",
                                  right: "-3px",
                                  bottom: "20px",
                                  border: "2px dashed #7c6bdb",
                                  borderRadius: "8px",
                                  pointerEvents: "none",
                                  zIndex: 1,
                                }}
                              />
                            )}

                            {/* Stacked bar */}
                            <div
                              style={{
                                width: `${barWidth}px`,
                                display: "flex",
                                flexDirection: "column-reverse",
                                cursor: "pointer",
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              {(() => {
                                const keys = Object.keys(
                                  stackedBarChartData.colors,
                                );
                                const nonZeroKeys = keys.filter(
                                  (k) => (data[k] || 0) > 0,
                                );

                                //  Calculate total filled height of this bar
                                const totalValue = keys.reduce(
                                  (sum, key) => sum + (data[key] || 0),
                                  0,
                                );
                                const totalHeight = getBarHeight(totalValue);

                                //  Empty part height (to fill remaining space up to full bar height)
                                const emptyHeight =
                                  stackedBarChartHeight -
                                  getBarHeight(
                                    Math.min(
                                      totalValue,
                                      stackedBarChartData.maxValue,
                                    ),
                                  );

                                return (
                                  <div
                                    style={{
                                      width: `${barWidth}px`,
                                      height: `${stackedBarChartHeight}px`,
                                      display: "flex",
                                      flexDirection: "column-reverse",
                                      backgroundColor: "#EDE3FF", //  background as empty area
                                      borderRadius: "6px", //  rounded full bar container
                                      overflow: "hidden", //  clip inner segments to rounded container
                                      cursor: "pointer",
                                      position: "relative",
                                      zIndex: 2,
                                    }}
                                  >
                                    {/*  Filled colored segments */}
                                    {keys.map((statusKey, i) => {
                                      const height = getBarHeight(
                                        data[statusKey] || 0,
                                      );
                                      if (height === 0) return null;

                                      const isTop =
                                        statusKey ===
                                        nonZeroKeys[nonZeroKeys.length - 1];
                                      const isBottom =
                                        statusKey === nonZeroKeys[0];
                                      const isSingleSegment =
                                        nonZeroKeys.length === 1;

                                      return (
                                        <div
                                          key={statusKey}
                                          style={{
                                            width: "100%",
                                            height: `${height}px`,
                                            backgroundColor:
                                              stackedBarChartData.colors[
                                                statusKey
                                              ],
                                            borderTopLeftRadius: isTop
                                              ? "6px"
                                              : 0,
                                            borderTopRightRadius: isTop
                                              ? "6px"
                                              : 0,
                                            borderBottomLeftRadius: isBottom
                                              ? "6px"
                                              : 0,
                                            borderBottomRightRadius: isBottom
                                              ? "6px"
                                              : 0,
                                            borderRadius: isSingleSegment
                                              ? "6px"
                                              : 0,
                                            transition: "height 0.4s ease",
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>

                            <div
                              style={{
                                marginTop: "8px",
                                fontSize: "12px",
                                color: "#666",
                                fontWeight: "400",
                              }}
                            >
                              {month}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Tooltip */}
                    {hoveredBar !== null && (
                      <div
                        style={{
                          position: "absolute",
                          top: "-70px",
                          left: `${
                            hoveredBar * (barWidth + barSpacing) + 50
                          }px`,
                          display: "flex",
                          alignItems: "flex-end",
                          gap: "20px",
                          zIndex: 1000,
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            minWidth: "120px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#7c6bdb",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            {stackedBarChartData.months[hoveredBar]}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            {Object.keys(stackedBarChartData.colors).map(
                              (key, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "12px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      backgroundColor:
                                        stackedBarChartData.colors[key],
                                      marginRight: "8px",
                                    }}
                                  />
                                  <span
                                    style={{
                                      color: "#666",
                                      marginRight: "auto",
                                    }}
                                  >
                                    {tooltipLabels[key] || key}
                                  </span>

                                  <span
                                    style={{
                                      fontWeight: "600",
                                      color: "#333",
                                      marginLeft: "30px",
                                    }}
                                  >
                                    {stackedBarChartData.data[hoveredBar][key]}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* final progress bar  */}
        <div className="col-xl-6 mt-3">
          <div className="bg-white d-flex align-items-center p-3 rounded-4 h-100">
            <div className="w-100">
              <h6 className="fw-bold mb-3 text-dark">Leads conversion rate</h6>

              <div
                className="rounded-pill overflow-hidden mb-4"
                style={{ height: "16px", backgroundColor: "#f0f0f0" }}
              >
                <div
                  className="h-100"
                  style={{
                    width: `${winPercent}%`,
                    backgroundColor: "#59d98e",
                    display: "inline-block",
                  }}
                ></div>
                <div
                  className="h-100"
                  style={{
                    width: `${lostPercent}%`,
                    backgroundColor: "#f08080",
                    display: "inline-block",
                  }}
                ></div>
              </div>

              <div
                className="p-3 mb-3 rounded-4 d-flex align-items-center"
                style={{ backgroundColor: "#e6fff0" }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: "#59d98e",
                    marginRight: "10px",
                  }}
                ></div>
                <div>
                  <p className="mb-0 text-muted small">Win Rate (%)</p>
                  <h5 className="fw-bold text-success">{winRate}%</h5>
                </div>
              </div>

              <div
                className="p-3 rounded-4 d-flex align-items-center"
                style={{ backgroundColor: "#ffecec" }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: "#f08080",
                    marginRight: "10px",
                  }}
                ></div>
                <div>
                  <p className="mb-0 text-muted small">Lost Rate (%)</p>
                  <h5 className="fw-bold text-danger">{lostRate}%</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
